import { useState, useEffect, useRef } from 'react';

export default function DefinitionSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredComponent, setHoveredComponent] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const components = [
    {
      title: 'Brand Strategy',
      description: 'Defining your purpose, mission, and unique promise.',
      icon: 'ri-compass-3-line'
    },
    {
      title: 'Brand Positioning',
      description: 'Differentiating you in the market.',
      icon: 'ri-target-line'
    },
    {
      title: 'Brand Messaging',
      description: 'Crafting your voice and communication pillars.',
      icon: 'ri-message-3-line'
    },
    {
      title: 'Brand Architecture',
      description: 'Structuring brands and sub-brands cohesively.',
      icon: 'ri-building-line'
    },
    {
      title: 'Brand Storytelling',
      description: 'Creating a relatable narrative that connects emotionally.',
      icon: 'ri-book-open-line'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('/assets/brand-dna-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            More Than a Logo: It's Your 
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Brand's DNA</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Strategic branding shapes your brand's vision, mission, and values, ensuring consistency across 
            all touchpoints. We define your unique voice and messaging to build trust and reinforce your 
            brand's core identity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {components.map((component, index) => (
            <div
              key={index}
              className={`group bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredComponent(index)}
              onMouseLeave={() => setHoveredComponent(null)}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                  <i className={`${component.icon} text-2xl text-white`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors duration-300">
                  {component.title}
                </h3>
                
                <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                  {component.description}
                </p>

                <div className={`mt-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0`}>
                  <i className="ri-arrow-right-line text-purple-400 text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}